'use client';

import { AlertTriangle, Database } from 'lucide-react';
import { type ReactNode, useEffect, useMemo, useState } from 'react';

import { cn } from '@/lib/utils';

type HealthResponse = {
    portalDb: { ok: boolean; message?: string };
    plantDb: {
        ok: boolean;
        checked: boolean;
        message?: string;
        connectionName?: string;
        checkedConnections?: number;
        failingConnections?: number;
        connections?: Array<{
            id: number;
            name: string;
            host?: string;
            port?: number;
            ok: boolean;
            message?: string;
            isDefault?: boolean;
            networks?: Array<{ id: number; name: string; dbName: string }>;
        }>;
    };
};

const withTimeout = async <T,>(promise: Promise<T>, ms: number): Promise<T> => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const timeoutPromise = new Promise<never>((_, reject) => {
        timeoutId = setTimeout(() => reject(new Error(`timeout after ${ms}ms`)), ms);
    });
    try {
        return await Promise.race([promise, timeoutPromise]);
    } finally {
        if (timeoutId) clearTimeout(timeoutId);
    }
};

export function HealthBanner({ className }: { className?: string }) {
    const [data, setData] = useState<HealthResponse | null>(null);
    const [, setError] = useState<string | null>(null);

    const issues = useMemo(() => {
        if (!data || !data.portalDb || !data.plantDb) return null;
        const list: Array<{
            key: 'portal' | 'plant';
            title: string;
            detail: ReactNode;
            tone: 'error' | 'warn';
        }> = [];
        if (!data.portalDb.ok) {
            list.push({
                key: 'portal',
                title: 'Portal-DB nicht erreichbar',
                detail: data.portalDb.message ?? 'Bitte Backend/DB prüfen.',
                tone: 'error',
            });
        }
        if (data.plantDb.checked && !data.plantDb.ok) {
            const name = data.plantDb.connectionName ? ` („${data.plantDb.connectionName}“) ` : ' ';
            const statuses = data.plantDb.connections ?? null;
            const failing = statuses ? statuses.filter((s) => !s.ok) : null;
            const showDetails = !!failing && failing.length > 0;
            list.push({
                key: 'plant',
                title: `Plant-DB${name}nicht erreichbar`,
                detail: showDetails ? (
                    <div className="space-y-2">
                        <div>{data.plantDb.message ?? 'Bitte Plant-DB Verbindung prüfen.'}</div>
                        <div className="space-y-2">
                            {failing.map((f) => {
                                const networks = f.networks ?? [];
                                const max = 6;
                                const shown = networks.slice(0, max);
                                const remaining = Math.max(0, networks.length - shown.length);
                                return (
                                    <div key={f.id} className="rounded-md border border-amber-200/60 bg-white/40 px-3 py-2">
                                        <div className="font-medium">
                                            Verbindung: {f.name}
                                            {f.isDefault ? <span className="ml-2 rounded bg-amber-200/70 px-2 py-0.5 text-xs">Standard</span> : null}
                                        </div>
                                        {f.host && f.port ? (
                                            <div className="text-xs opacity-80">
                                                Host: <span className="font-mono">{f.host}:{f.port}</span>
                                            </div>
                                        ) : null}
                                        <div className="text-sm opacity-90">{f.message ?? 'Verbindung fehlgeschlagen.'}</div>
                                        {shown.length > 0 ? (
                                            <div className="mt-2 text-sm">
                                                <div className="font-medium">Betroffene Datenbanken</div>
                                                <ul className="list-disc pl-5">
                                                    {shown.map((n) => (
                                                        <li key={n.id}>
                                                            {n.name}: <span className="font-mono text-xs">{n.dbName}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                                {remaining > 0 ? (
                                                    <div className="text-xs opacity-80">+{remaining} weitere</div>
                                                ) : null}
                                            </div>
                                        ) : (
                                            <div className="mt-2 text-xs opacity-80">Keine aktiven Netze verwenden diese Verbindung.</div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ) : (
                    <div>{data.plantDb.message ?? 'Bitte Plant-DB Verbindung prüfen.'}</div>
                ),
                tone: 'warn',
            });
        }
        return list;
    }, [data]);

    useEffect(() => {
        let alive = true;

        const load = async () => {
            try {
                setError(null);
                const res = await withTimeout(fetch('/api/health', { cache: 'no-store' }), 6_000);
                const json = (await withTimeout(res.json(), 4_000)) as HealthResponse;
                if (!alive) return;
                setData(json);
            } catch (e) {
                if (!alive) return;
                const message = e instanceof Error ? e.message : String(e);
                setError(message.toLowerCase().includes('timeout') ? 'Health-Check Timeout' : message);
            }
        };

        void load();
        const interval = window.setInterval(load, 30_000);
        return () => {
            alive = false;
            window.clearInterval(interval);
        };
    }, []);

    if (issues && issues.length > 0) {
        return (
            <div className={cn('mx-4 mt-4 rounded-lg border px-4 py-3', className, issues.some((i) => i.tone === 'error') ? 'border-red-200 bg-red-50 text-red-950' : 'border-amber-200 bg-amber-50 text-amber-950')}>
                <div className="flex items-start gap-3">
                    <div className="mt-0.5">
                        {issues.some((i) => i.tone === 'error') ? (
                            <AlertTriangle className="h-5 w-5 text-red-600" />
                        ) : (
                            <Database className="h-5 w-5 text-amber-700" />
                        )}
                    </div>
                    <div className="flex-1 space-y-2">
                        {issues.map((issue) => (
                            <div key={issue.key}>
                                <div className="font-semibold">{issue.title}</div>
                                <div className="text-sm opacity-90">{issue.detail}</div>
                            </div>
                        ))}
                        <div className="text-xs opacity-80">
                            Hinweis: Bitte prüfen Sie DB-Verbindungen, Netzwerk/Host-Auflösung und Container-Status. Diese Meldung aktualisiert sich automatisch.
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return null;
}
